import { Modal, Button, Form, InputGroup } from "react-bootstrap";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { API_BASE_URL } from "../config";
import "../css/login.css";

export default function LoginModal({ show, handleClose, onSuccess }) {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!show) return;
    setEmail("");
    setPassword("");
    setError("");
    setLoading(false);
  }, [show]);

  async function handleLogin(e) {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      const payloadText = await res.text();
      const payload = payloadText ? JSON.parse(payloadText) : null;

      if (!res.ok) {
        const msg = payload?.message || `HTTP ${res.status}`;
        throw new Error(msg);
      }

      const p = payload;
      const user = {
        id: p.id,
        firstName: p.firstName,
        lastName: p.lastName,
        username: p.username,
        email: p.email,
        city: p.city,
        state: p.state,
        role: (p.role || "").toUpperCase(),
      };
      const token = p.token ?? null;

      if (!user.role) throw new Error("Missing role in response.");

      login(user, token);
      onSuccess?.(user);
      handleClose?.();

      if (user.role === "ADMIN") navigate("/admin");
      else navigate("/");
    } catch (err) {
      console.error(err);
      setError(err.message || "Invalid credentials. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  if (!show) return null;

  return (
    <Modal show={show} onHide={handleClose} centered className="auth-modal">
      <Modal.Header closeButton className="auth-header">
        <Modal.Title className="auth-title">Login</Modal.Title>
      </Modal.Header>
      <Modal.Body className="auth-body">
        <Form onSubmit={handleLogin} className="auth-form" autoComplete="off">
          <input type="text" name="fake-user" autoComplete="username" style={{ display: "none" }} />
          <input type="password" name="fake-pass" autoComplete="new-password" style={{ display: "none" }} />

          <Form.Group className="mb-3" controlId="loginEmail">
            <Form.Label className="text-light-soft">Email</Form.Label>
            <Form.Control
              className="auth-input"
              type="email"
              placeholder="Enter email"
              required
              autoComplete="off"
              inputMode="email"
              spellCheck="false"
              autoCapitalize="none"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="loginPassword">
            <Form.Label className="text-light-soft">Password</Form.Label>
            <InputGroup>
              <Form.Control
                className="auth-input"
                type={showPwd ? "text" : "password"}
                placeholder="Password"
                required
                minLength={8}
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <Button
                variant="outline-light"
                type="button"
                className="auth-eye"
                aria-label={showPwd ? "Hide password" : "Show password"}
                onClick={() => setShowPwd((s) => !s)}
              >
                {showPwd ? "Hide" : "Show"}
              </Button>
            </InputGroup>
            <Form.Text className="text-light-dimmer">Minimum 8 characters.</Form.Text>
          </Form.Group>

          {error && <p className="text-center text-danger fw-semibold">{error}</p>}

          <div className="d-flex gap-2 justify-content-between mt-3">
            <Button type="submit" className="btn-auth-primary" disabled={loading}>
              {loading ? "Logging in..." : "Login"}
            </Button>
            <Button
              type="button"
              className="btn-auth-secondary"
              onClick={() => {
                handleClose?.();
                navigate("/register");
              }}
            >
              Register
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
}
